export type Tab = 'contacts' | 'stories' | 'authors'

export type Entity = { url?: string; name?: string; label?: string }

export type StoryItem = { id: number; count: number | null; date: string | null }
export type ContactItem = { id: number; lastUpdate: string | null }
export type AuthorItem = { id: number; count: number }

export type RowItem = {
  id: number
  count?: number | null
  date?: string | null
  lastUpdate?: string | null
}

export type Entry = {
  eyebrow: string | null
  title: string
  subtitle: string
  summary: string
  stories: StoryItem[]
  contacts: ContactItem[]
  authors: AuthorItem[]
}

export type Payload = {
  stories: Record<string, Entity>
  contacts: Record<string, Entity>
  authors: Record<string, Entity>
  entries: Record<string, Entry>
}

const EMPTY_TEXT = 'No available data.'
const NO_CONTACTS_TEXT = 'No contacts are tagged against this tracker for these stories.'
const NO_STORIES_TEXT = 'No stories are recorded against this tracker for this period.'

type TabConfig = {
  heading: [string, string]
  emptyText: string
  items: (entry: Entry) => RowItem[]
  label: (item: RowItem, payload: Payload | null) => string
  url: (item: RowItem, payload: Payload | null) => string
  meta: (item: RowItem) => string
}

const TAB_CONFIG: Record<Tab, TabConfig> = {
  contacts: {
    heading: ['Contact', 'Latest update'],
    emptyText: NO_CONTACTS_TEXT,
    items: (entry) => entry.contacts,
    label: (item, payload) => payload?.contacts?.[item.id]?.name ?? '',
    url: (item, payload) => payload?.contacts?.[item.id]?.url ?? '#',
    meta: (item) => item.lastUpdate ?? ''
  },

  stories: {
    heading: ['Story', 'Date'],
    emptyText: NO_STORIES_TEXT,
    items: (entry) => entry.stories,
    label: (item, payload) => payload?.stories?.[item.id]?.label ?? '',
    url: (item, payload) => payload?.stories?.[item.id]?.url ?? '#',
    meta: (item) => item.date ?? ''
  },

  authors: {
    heading: ['Author', 'Updates'],
    emptyText: EMPTY_TEXT,
    items: (entry) => entry.authors,
    label: (item, payload) => payload?.authors?.[item.id]?.name ?? '',
    url: (item, payload) => payload?.authors?.[item.id]?.url ?? '#',
    meta: (item) => String(item.count ?? 0)
  }
}

export type MetricDialogElements = {
  dialog: HTMLDialogElement
  payload: HTMLScriptElement
  eyebrow: HTMLElement
  title: HTMLElement
  subtitle: HTMLElement
  summary: HTMLElement
  table: HTMLTableElement
  heading: HTMLTableSectionElement
  headingLabel: HTMLTableCellElement
  headingMeta: HTMLTableCellElement
  body: HTMLTableSectionElement
  empty: HTMLElement
  tabs: HTMLButtonElement[]
}

export function createMetricDialogPanel(elements: MetricDialogElements) {
  let parsed: Payload | null = null
  let entry: Entry | null = null

  const payload = (): Payload | null => {
    if (parsed) return parsed

    try {
      parsed = JSON.parse(elements.payload.textContent ?? '{}') as Payload
    } catch (error) {
      console.error('metric-dialog: could not parse payload', error)
      parsed = null
    }

    return parsed
  };

  const markTabSelected = (tab: Tab) => {
    for (const target of elements.tabs) {
      target.setAttribute('aria-selected', String(target.dataset.tab === tab))
    }
  };

  const renderContent = (config: TabConfig, hasRows: boolean) => {
    elements.headingLabel.textContent = config.heading[0]
    elements.headingMeta.textContent = config.heading[1]

    elements.heading.hidden = !hasRows
    elements.table.hidden = !hasRows

    elements.empty.textContent = config.emptyText
    elements.empty.hidden = hasRows
  };

  const buildRow = (tab: Tab, config: TabConfig, item: RowItem) => {
    const row = document.createElement('tr')
    row.className = `metric-dialog__row metric-dialog__row--${tab}`

    const label = document.createElement('td')

    const link = document.createElement('a')
    link.className = 'metric-dialog__link'
    link.href = config.url(item, payload())
    link.target = '_blank'
    link.rel = 'noopener'
    link.dataset.skipAjaxLoading = 'true'
    link.textContent = config.label(item, payload())
    label.append(link)
    row.append(label)

    const metaText = config.meta(item)
    if (metaText) {
      const meta = document.createElement('td')
      meta.className = 'metric-dialog__meta'
      meta.textContent = metaText
      row.append(meta)
    }

    return row
  };

  const buildRows = (tab: Tab, config: TabConfig, items: RowItem[]) => {
    const fragment = document.createDocumentFragment()

    for (const item of items) {
      fragment.append(buildRow(tab, config, item))
    }


    return fragment
  };

  const showTab = (tab: Tab) => {
    if (!entry) return

    const config = TAB_CONFIG[tab]
    const items = config.items(entry)

    markTabSelected(tab)
    renderContent(config, items.length > 0)
    elements.body.replaceChildren(buildRows(tab, config, items))
  };

  return {
    showTab,

    open(key: string | undefined) {
      if (!key) return false

      const found = payload()?.entries?.[key]
      if (!found) return false

      entry = found
      elements.eyebrow.textContent = found.eyebrow ?? ''
      elements.title.textContent = found.title
      elements.subtitle.textContent = found.subtitle
      elements.summary.textContent = found.summary

      showTab('contacts')

      return true
    },

    close() {
      elements.dialog.close()
    }
  }
}
